import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import {
  AngleProcessingAvailability,
  CornerProcessingHelper,
  CuttingFacadeOutline,
  CuttingPanelHelper,
  MaterialType,
  PositionMaxSize,
} from 'cloud-shared-lib';

import { CuttingButt } from '../../models/cutting/cutting-butt.model';
import { CuttingPanelBaseSlot } from '../../models/cutting/cutting-panel-base-slot.model';
import {
  AngleMode,
  AnglePosition,
  CuttingPanelOutline,
  MasonsMitreOrientationEnum,
  OutlineAngle,
} from '../../models/cutting/cutting-panel-outline.model';
import { PanelBase } from '../../models/cutting/panel-base.model';
import { OutlineEditorOptions } from '../../models/user/outline-editor-options.model';

export class AngleSettingsBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _AngleSettingsMixinBase = mixinDisabled(AngleSettingsBase);

@Component({
  selector: 'csl-angle-settings',
  templateUrl: './angle-settings.component.html',
  styleUrls: ['./angle-settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
})
export class AngleSettingsComponent extends _AngleSettingsMixinBase implements OnChanges, CanDisable {
  @HostBinding('class.angle-settings')
  angleSettings = true;

  @Input() angleProcessingAvailability: AngleProcessingAvailability;
  @Input() panel: PanelBase;
  @Input() anglePosition: AnglePosition = 'tl';
  @Input() materialType: MaterialType;
  @Input() outlineOptions: OutlineEditorOptions;
  @Input()
  get dense(): boolean {
    return this._dense;
  }
  set dense(value: BooleanInput) {
    this._dense = coerceBooleanProperty(value);
  }

  thisAngleMode = AngleMode;

  topLeftButts: CuttingButt[] = [];
  topRightButts: CuttingButt[] = [];
  bottomRightButts: CuttingButt[] = [];
  bottomLeftButts: CuttingButt[] = [];

  thisMasonsMitreOrientationEnum = MasonsMitreOrientationEnum;

  _dense = false;

  get butts(): CuttingButt[] {
    switch (this.anglePosition) {
      case 'tl':
        return this.topLeftButts;

      case 'tr':
        return this.topRightButts;

      case 'br':
        return this.bottomRightButts;

      case 'bl':
        return this.bottomLeftButts;

      default:
        return [];
    }
  }

  get xMax(): number {
    const allXMax = CornerProcessingHelper.getAllXMax(this.outline, this.panel);
    switch (this.anglePosition) {
      case 'tl':
        return allXMax[PositionMaxSize.TopLeft];

      case 'tr':
        return allXMax[PositionMaxSize.TopRight];

      case 'br':
        return allXMax[PositionMaxSize.BottomRight];

      case 'bl':
        return allXMax[PositionMaxSize.BottomLeft];

      default:
        return 0;
    }
  }

  get yMax(): number {
    const allYMax = CornerProcessingHelper.getAllYMax(this.outline, this.panel);
    switch (this.anglePosition) {
      case 'tl':
        return allYMax[PositionMaxSize.TopLeft];

      case 'tr':
        return allYMax[PositionMaxSize.TopRight];

      case 'br':
        return allYMax[PositionMaxSize.BottomRight];

      case 'bl':
        return allYMax[PositionMaxSize.BottomLeft];

      default:
        return 0;
    }
  }

  get max(): number {
    switch (this.anglePosition) {
      case 'tl':
        return this.topLeftMax;

      case 'tr':
        return this.topRightMax;

      case 'br':
        return this.bottomRightMax;

      case 'bl':
        return this.bottomLeftMax;

      default:
        return 0;
    }
  }

  get outline(): CuttingPanelOutline<CuttingPanelBaseSlot> | CuttingFacadeOutline {
    return this.panel.panelOutline;
  }

  get angle(): OutlineAngle {
    switch (this.anglePosition) {
      case 'tl':
        return this.outline.topLeft;

      case 'tr':
        return this.outline.topRight;

      case 'br':
        return this.outline.bottomRight;

      case 'bl':
        return this.outline.bottomLeft;
    }
  }

  get topLeftMax(): number {
    const topLeftMax = CornerProcessingHelper.topLeftMax(this.outline, this.panel);
    return Math.min(topLeftMax.x, topLeftMax.y);
  }
  get topRightMax(): number {
    const topRightMax = CornerProcessingHelper.topRightMax(this.outline, this.panel);
    return Math.min(topRightMax.x, topRightMax.y);
  }
  get bottomRightMax(): number {
    const bottomRightMax = CornerProcessingHelper.bottomRightMax(this.outline, this.panel);
    return Math.min(bottomRightMax.x, bottomRightMax.y);
  }
  get bottomLeftMax(): number {
    const bottomLeftMax = CornerProcessingHelper.bottomRightMax(this.outline, this.panel);
    return Math.min(bottomLeftMax.x, bottomLeftMax.y);
  }

  get disableButt(): boolean {
    return (
      this.angle.mode === AngleMode.None ||
      this.angle.mode === AngleMode.MasonsMitreMale ||
      this.angle.mode === AngleMode.MasonsMitreFemale ||
      (this.angle.mode === AngleMode.CuttedInside &&
        !this.outlineOptions.angles.insideCutting.restrictions.addButtToCutting)
    );
  }

  @Output() anglesChange: EventEmitter<CuttingPanelOutline<CuttingPanelBaseSlot> | CuttingFacadeOutline> =
    new EventEmitter();

  constructor() {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.panel?.currentValue) {
      this.topLeftButts = this.getButtListForAngle(this.panel.l1, this.panel.w1, this.outline.topLeft.butt);
      this.topRightButts = this.getButtListForAngle(this.panel.l1, this.panel.w2, this.outline.topRight.butt);
      this.bottomRightButts = this.getButtListForAngle(this.panel.l2, this.panel.w2, this.outline.bottomRight.butt);
      this.bottomLeftButts = this.getButtListForAngle(this.panel.l2, this.panel.w1, this.outline.bottomLeft.butt);
    }
  }

  getButtListForAngle(
    firstSideButt: CuttingButt,
    secondSideButt: CuttingButt,
    buttOnAngle: CuttingButt
  ): CuttingButt[] {
    const butts: CuttingButt[] = [];

    const compareWithFirst: boolean = CuttingPanelHelper.compareButts(firstSideButt, buttOnAngle);
    const compareWithSecond: boolean = CuttingPanelHelper.compareButts(secondSideButt, buttOnAngle);
    const compareAdjacent: boolean = CuttingPanelHelper.compareButts(secondSideButt, firstSideButt);

    if (firstSideButt) {
      butts.push(compareWithFirst ? buttOnAngle : firstSideButt);
    }
    if (secondSideButt && !compareAdjacent) {
      butts.push(compareWithSecond ? buttOnAngle : secondSideButt);
    }

    if (!compareWithFirst && !compareWithSecond && buttOnAngle) {
      butts.push(buttOnAngle);
    }

    return butts;
  }

  setSingleButt(): void {
    if (this.angle.mode === AngleMode.None) {
      this.angle.butt = undefined;
    } else if (!this.angle.butt && this.butts.length === 1) {
      this.angle.butt = this.butts[0];
    }
  }

  clearOtherModeValues(): void {
    if (this.angle.mode !== AngleMode.Chamfer) {
      this.angle.chamferLength = 0;
    }

    if (this.angle.mode !== AngleMode.CuttedInside) {
      this.angle.cutInsideX = 0;
      this.angle.cutInsideY = 0;
      this.angle.cutInsideR = 0;
    }

    if (this.angle.mode !== AngleMode.CuttedInsideWithAngle) {
      this.angle.cutInsideWithAngle.x = 0;
      this.angle.cutInsideWithAngle.y = 0;
      this.angle.cutInsideWithAngle.r = 0;
      this.angle.cutInsideWithAngle.xA = 90;
      this.angle.cutInsideWithAngle.yA = 90;
      this.angle.cutInsideWithAngle.x1 = 0;
      this.angle.cutInsideWithAngle.y1 = 0;
    }

    if (this.angle.mode !== AngleMode.Cutted) {
      this.angle.cutX = 0;
      this.angle.cutY = 0;
    }

    if (this.angle.mode !== AngleMode.Rounded) {
      this.angle.rounding = 0;
    }

    if (this.angle.mode !== AngleMode.RoundedInside) {
      this.angle.roundingInside = 0;
    }

    if (this.angle.mode !== AngleMode.MasonsMitreFemale) {
      this.angle.masonsMitreFemale.length = 0;
      this.angle.masonsMitreFemale.orientation = 0;
    }

    if (this.angle.mode !== AngleMode.MasonsMitreMale) {
      this.angle.masonsMitreMale.orientation = 0;
    }
  }

  getMin(...values: number[]): number {
    return Math.min(...values);
  }

  getMax(...values: number[]): number {
    return Math.max(...values);
  }

  setMasonsMitreParams(): void {
    this.angle.masonsMitreFemale.allowance = this.outlineOptions.angles.masonsMitre.restrictions.allowance;
    this.angle.masonsMitreFemale.radius = this.outlineOptions.angles.masonsMitre.restrictions.radius;

    this.angle.masonsMitreMale.allowance = this.outlineOptions.angles.masonsMitre.restrictions.allowance;
    this.angle.masonsMitreMale.radius = this.outlineOptions.angles.masonsMitre.restrictions.radius;
  }
}
